/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { getAuth } from 'firebase/auth';
import { useCollection } from '../../contexts/CollectionContext.tsx';

interface CollectionsSyncButtonProps {
  isAdmin: boolean;
}

export default function CollectionsSyncButton({ isAdmin }: CollectionsSyncButtonProps) {
  const { refreshCollections } = useCollection();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    totalCount: number;
    message: string;
    durationMs?: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSync = async () => {
    setIsLoading(true);
    setResult(null);
    setError(null);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Get the current user's ID token
      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/collections-sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        // Refresh collections through context
        if (refreshCollections) {
          await refreshCollections();
        }
      } else {
        setError(data.error || 'Sync failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show button if not admin
  if (!isAdmin) {
    console.log('Not showing collections sync button because isAdmin is false');
    return null;
  }

  console.log('Rendering the collections sync button!');

  return (
     <div>

      <button
        onClick={handleSync}
        disabled={isLoading}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {isLoading ? 'Syncing Collections...' : 'Sync Collections'}
      </button>

      {isLoading && (
        <div className="mt-4 text-sm text-gray-600">
          <p>⏳ Syncing collections from Shopify...</p>
          <p className="mt-1 text-xs">This should only take a few seconds.</p>
        </div>
      )}

      {result && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
          <p className="text-green-800 font-semibold">✅ Sync Completed!</p>
          <p className="text-sm text-green-700 mt-1">
            Total collections synced: {result.totalCount}
          </p>
          {result.durationMs && (
            <p className="text-xs text-green-600 mt-1">
              Completed in {(result.durationMs / 1000).toFixed(1)} seconds
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-red-800 font-semibold">❌ Sync Failed</p>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}
    </div>
  );
}